import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  TextField,
  InputAdornment,
  Chip,
  IconButton,
  Button,
  Typography,
  Menu,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Skeleton,
  Avatar,
  Tooltip,
  Checkbox,
} from '@mui/material';
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Add as AddIcon,
  MoreVert as MoreIcon,
  Visibility as ViewIcon,
  Edit as EditIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  AccountTree as HierarchyIcon,
  PeopleOutlined as PeopleIcon,
  DownloadOutlined as DownloadIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams, Link as RouterLink } from 'react-router-dom';
import { customerApi, settingsApi, userApi } from '../../api/client';
import { Customer, Segment, User, CustomerStatus, GeoCountryOption } from '../../types';
import { countryLabel } from '../../utils/countries';
import { statusColors, statusLabels } from '../../theme';
import PageHeader from '../../components/layout/PageHeader';
import { format } from 'date-fns';
import { da } from 'date-fns/locale';

type SortKey = 'name' | 'segment' | 'status' | 'assignedTo' | 'latestActivity' | 'latestBooking';
type SortOrder = 'asc' | 'desc';

export const CustomerListPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [segments, setSegments] = useState<Segment[]>([]);
  const [industries, setIndustries] = useState<string[]>([]);
  const [countries, setCountries] = useState<string[]>([]);
  const [countryFilter, setCountryFilter] = useState<string[]>(searchParams.get('country') ? searchParams.get('country')!.split(',').filter(Boolean) : []);
  const [dispatchers, setDispatchers] = useState<Pick<User, 'id' | 'firstName' | 'lastName' | 'email' | 'role'>[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(Number(searchParams.get('page')) || 0);
  const [rowsPerPage, setRowsPerPage] = useState(Number(searchParams.get('limit')) || 20);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState(searchParams.get('q') || '');
  const [debouncedSearch, setDebouncedSearch] = useState(searchParams.get('q') || '');
  const parseCsvParam = (key: string): string[] => {
    const v = searchParams.get(key);
    return v ? v.split(',').filter(Boolean) : [];
  };
  const [statusFilter, setStatusFilter] = useState<string[]>(parseCsvParam('status'));
  const [segmentFilter, setSegmentFilter] = useState<string[]>(parseCsvParam('segment'));
  const [industryFilter, setIndustryFilter] = useState<string[]>(parseCsvParam('industry'));
  const [assignedFilter, setAssignedFilter] = useState<string[]>(parseCsvParam('assigned'));
  const [hierarchyFilter, setHierarchyFilter] = useState(searchParams.get('hierarchy') || '');
  // Geo filters derived from transports (ISO-2 country codes)
  const [operatesFilter, setOperatesFilter] = useState<string[]>(parseCsvParam('operates'));
  const [originCountryFilter, setOriginCountryFilter] = useState<string[]>(parseCsvParam('originCountry'));
  const [destCountryFilter, setDestCountryFilter] = useState<string[]>(parseCsvParam('destCountry'));
  const [geoCountries, setGeoCountries] = useState<GeoCountryOption[]>([]);
  const [letterFilter, setLetterFilter] = useState(searchParams.get('letter') || '');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [contactAnchorEl, setContactAnchorEl] = useState<null | HTMLElement>(null);
  const [contactMenuType, setContactMenuType] = useState<'phone' | 'email'>('phone');
  const [contactMenuCustomer, setContactMenuCustomer] = useState<Customer | null>(null);
  const [sortBy, setSortBy] = useState<SortKey>((searchParams.get('sortBy') as SortKey) || 'name');
  const [sortOrder, setSortOrder] = useState<SortOrder>((searchParams.get('sortOrder') as SortOrder) || 'asc');

  // Sync state to URL params (so back-navigation preserves filters)
  useEffect(() => {
    const params: Record<string, string> = {};
    if (debouncedSearch) params.q = debouncedSearch;
    if (statusFilter.length) params.status = statusFilter.join(',');
    if (segmentFilter.length) params.segment = segmentFilter.join(',');
    if (industryFilter.length) params.industry = industryFilter.join(',');
    if (assignedFilter.length) params.assigned = assignedFilter.join(',');
    if (countryFilter.length) params.country = countryFilter.join(',');
    if (operatesFilter.length) params.operates = operatesFilter.join(',');
    if (originCountryFilter.length) params.originCountry = originCountryFilter.join(',');
    if (destCountryFilter.length) params.destCountry = destCountryFilter.join(',');
    if (hierarchyFilter) params.hierarchy = hierarchyFilter;
    if (letterFilter) params.letter = letterFilter;
    if (sortBy !== 'name') params.sortBy = sortBy;
    if (sortOrder !== 'asc') params.sortOrder = sortOrder;
    if (page > 0) params.page = String(page);
    if (rowsPerPage !== 20) params.limit = String(rowsPerPage);
    setSearchParams(params, { replace: true });
  }, [debouncedSearch, statusFilter, segmentFilter, industryFilter, assignedFilter, countryFilter, operatesFilter, originCountryFilter, destCountryFilter, hierarchyFilter, letterFilter, sortBy, sortOrder, page, rowsPerPage, setSearchParams]);

  // Debounce search input (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [statusFilter, segmentFilter, industryFilter, assignedFilter, countryFilter, operatesFilter, originCountryFilter, destCountryFilter, hierarchyFilter, letterFilter]);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        page: page + 1,
        limit: rowsPerPage,
        sortBy,
        sortOrder,
      };
      if (debouncedSearch) params.search = debouncedSearch;
      if (statusFilter.length) params.status = statusFilter.join(',');
      if (segmentFilter.length) params.segmentId = segmentFilter.join(',');
      if (industryFilter.length) params.industry = industryFilter.join(',');
      if (assignedFilter.length) params.assignedToId = assignedFilter.join(',');
      if (countryFilter.length) params.country = countryFilter.join(',');
      if (operatesFilter.length) params.operatesInCountries = operatesFilter.join(',');
      if (originCountryFilter.length) params.originCountries = originCountryFilter.join(',');
      if (destCountryFilter.length) params.destinationCountries = destCountryFilter.join(',');
      if (hierarchyFilter) params.hierarchyFilter = hierarchyFilter;
      if (letterFilter) params.nameStartsWith = letterFilter;

      const response = await customerApi.getAll(params);
      setCustomers(response.data.data);
      setTotal(response.data.pagination?.total || 0);
    } catch (error) {
      console.error('Failed to fetch customers:', error);
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, debouncedSearch, statusFilter, segmentFilter, industryFilter, assignedFilter, countryFilter, operatesFilter, originCountryFilter, destCountryFilter, hierarchyFilter, letterFilter, sortBy, sortOrder]);

  useEffect(() => {
    fetchCustomers();
  }, [fetchCustomers]);

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [segmentsRes, dispatchersRes, industriesRes, countriesRes, geoRes] = await Promise.allSettled([
          settingsApi.getSegments(),
          userApi.getDispatchers(),
          customerApi.getIndustries(),
          customerApi.getCountries(),
          settingsApi.getSegmentGeoOptions(),
        ]);
        if (geoRes.status === 'fulfilled') setGeoCountries(geoRes.value.data.data?.countries || []);
        if (segmentsRes.status === 'fulfilled') setSegments(segmentsRes.value.data.data);
        else console.error('Segments failed:', segmentsRes.reason);
        if (dispatchersRes.status === 'fulfilled') setDispatchers(dispatchersRes.value.data.data);
        else console.error('Dispatchers failed:', dispatchersRes.reason);
        if (industriesRes.status === 'fulfilled') {
          const data = industriesRes.value.data.data || [];
          setIndustries(data.map((d: any) => typeof d === 'string' ? d : d.industry).filter(Boolean));
        }
        if (countriesRes.status === 'fulfilled') {
          const data = countriesRes.value.data.data || [];
          setCountries(data.map((d: any) => typeof d === 'string' ? d : d.country).filter(Boolean));
        }
      } catch (error) {
        console.error('Failed to fetch filters:', error);
      }
    };
    fetchFilters();
  }, []);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, customer: Customer) => {
    setAnchorEl(event.currentTarget);
    setSelectedCustomer(customer);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedCustomer(null);
  };

  const handleContactAction = (e: React.MouseEvent<HTMLElement>, customer: Customer, type: 'phone' | 'email') => {
    e.stopPropagation();
    const contacts = (customer as any).contacts || [];
    const field = type === 'phone' ? 'phone' : 'email';
    const customerValue = type === 'phone' ? customer.phone : customer.email;

    // Build list of options: customer-level + contacts with the relevant field
    const options: { label: string; value: string }[] = [];
    if (customerValue) {
      options.push({ label: customer.name, value: customerValue });
    }
    for (const c of contacts) {
      if (c[field]) {
        const name = [c.firstName, c.lastName].filter(Boolean).join(' ') || 'Kontakt';
        // Avoid duplicate if same as customer-level
        if (c[field] !== customerValue) {
          options.push({ label: name, value: c[field] });
        }
      }
    }

    if (options.length === 1) {
      const prefix = type === 'phone' ? 'tel:' : 'mailto:';
      window.location.href = `${prefix}${options[0].value}`;
    } else if (options.length > 1) {
      setContactMenuCustomer(customer);
      setContactMenuType(type);
      setContactAnchorEl(e.currentTarget);
    }
  };

  const handleContactMenuClose = () => {
    setContactAnchorEl(null);
    setContactMenuCustomer(null);
  };

  const handleStatusChange = async (customerId: string, status: CustomerStatus) => {
    try {
      await customerApi.updateStatus(customerId, status);
      fetchCustomers();
    } catch (error) {
      console.error('Failed to update status:', error);
    }
  };

  const handleSort = (column: SortKey) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder(column === 'name' ? 'asc' : 'desc');
    }
    setPage(0);
  };

  const columns: { id: SortKey; label: string; align?: 'left' | 'right' }[] = [
    { id: 'name', label: 'Kunde' },
    { id: 'segment', label: 'Segment' },
    { id: 'status', label: 'Status' },
    { id: 'assignedTo', label: 'Ansvarlig' },
    { id: 'latestActivity', label: 'Seneste aktivitet' },
    { id: 'latestBooking', label: 'Seneste booking' },
  ];

  return (
    <Box>
      <PageHeader
        title="Kunder"
        subtitle={
          loading
            ? 'Henter kunder…'
            : `${total} kunder i alt · ${customers.length} vist`
        }
        icon={<PeopleIcon sx={{ fontSize: 18 }} />}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              size="small"
              startIcon={<DownloadIcon sx={{ fontSize: '0.9rem' }} />}
              onClick={async () => {
                try {
                  const res = await customerApi.exportCsv();
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const link = document.createElement('a');
                  link.href = url;
                  link.download = `kundeliste-${new Date().toISOString().split('T')[0]}.csv`;
                  link.click();
                  window.URL.revokeObjectURL(url);
                } catch (e) {
                  console.error('Export failed:', e);
                }
              }}
            >
              Eksporter Excel
            </Button>
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: '0.9rem' }} />}
              onClick={() => navigate('/customers/new')}
            >
              Ny kunde
            </Button>
          </Box>
        }
      />

      {/* Filters */}
      <Card sx={{ mb: 1.5, p: 1.5 }}>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            placeholder="Søg kunder..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            size="small"
            sx={{ minWidth: 180 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: '1rem' }} />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel shrink>Status</InputLabel>
            <Select
              multiple
              value={statusFilter}
              onChange={(e) => setStatusFilter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              label="Status"
              renderValue={(selected) =>
                (selected as string[]).length === 0
                  ? 'Alle'
                  : (selected as string[]).map((v) => statusLabels[v] || v).join(', ')
              }
              displayEmpty
            >
              {Object.entries(statusLabels).map(([value, label]) => (
                <MenuItem key={value} value={value}>
                  <Checkbox checked={statusFilter.indexOf(value) > -1} size="small" sx={{ p: 0, mr: 1 }} />
                  {label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel shrink>Segment</InputLabel>
            <Select
              multiple
              value={segmentFilter}
              onChange={(e) => setSegmentFilter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              label="Segment"
              renderValue={(selected) => {
                const sel = selected as string[];
                if (sel.length === 0) return 'Alle';
                return sel.map((id) => segments.find((s) => s.id === id)?.name || id).join(', ');
              }}
              displayEmpty
            >
              {segments.map((segment) => (
                <MenuItem key={segment.id} value={segment.id}>
                  <Checkbox checked={segmentFilter.indexOf(segment.id) > -1} size="small" sx={{ p: 0, mr: 1 }} />
                  {segment.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel shrink>Branche</InputLabel>
            <Select
              multiple
              value={industryFilter}
              onChange={(e) => setIndustryFilter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              label="Branche"
              renderValue={(selected) => {
                const sel = selected as string[];
                return sel.length === 0 ? 'Alle' : sel.join(', ');
              }}
              displayEmpty
            >
              {industries.map((ind) => (
                <MenuItem key={ind} value={ind}>
                  <Checkbox checked={industryFilter.indexOf(ind) > -1} size="small" sx={{ p: 0, mr: 1 }} />
                  {ind}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel shrink>Land</InputLabel>
            <Select
              multiple
              value={countryFilter}
              onChange={(e) => setCountryFilter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              label="Land"
              renderValue={(selected) => {
                const sel = selected as string[];
                return sel.length === 0 ? 'Alle' : sel.join(', ');
              }}
              displayEmpty
            >
              {countries.map((c) => (
                <MenuItem key={c} value={c}>
                  <Checkbox checked={countryFilter.indexOf(c) > -1} size="small" sx={{ p: 0, mr: 1 }} />
                  {c}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {([
            { key: 'operates', label: 'Opererer i land', value: operatesFilter, set: setOperatesFilter },
            { key: 'origin', label: 'Afsenderland', value: originCountryFilter, set: setOriginCountryFilter },
            { key: 'dest', label: 'Modtagerland', value: destCountryFilter, set: setDestCountryFilter },
          ] as const).map((f) => (
            <FormControl key={f.key} size="small" sx={{ minWidth: 150 }}>
              <InputLabel shrink>{f.label}</InputLabel>
              <Select
                multiple
                value={f.value}
                onChange={(e) => f.set(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
                label={f.label}
                renderValue={(selected) => {
                  const sel = selected as string[];
                  return sel.length === 0 ? 'Alle' : sel.join(', ');
                }}
                displayEmpty
              >
                {geoCountries.map((c) => (
                  <MenuItem key={c.code} value={c.code}>
                    <Checkbox checked={f.value.indexOf(c.code) > -1} size="small" sx={{ p: 0, mr: 1 }} />
                    {countryLabel(c.code)}
                    <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 'auto', pl: 1 }}>
                      {c.asOrigin + c.asDestination}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ))}
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel shrink>Ansvarlig</InputLabel>
            <Select
              multiple
              value={assignedFilter}
              onChange={(e) => setAssignedFilter(typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
              label="Ansvarlig"
              renderValue={(selected) => {
                const sel = selected as string[];
                if (sel.length === 0) return 'Alle';
                return sel.map((id) => {
                  const u = dispatchers.find((d) => d.id === id);
                  return u ? `${u.firstName} ${u.lastName}` : id;
                }).join(', ');
              }}
              displayEmpty
            >
              {dispatchers.map((user) => (
                <MenuItem key={user.id} value={user.id}>
                  <Checkbox checked={assignedFilter.indexOf(user.id) > -1} size="small" sx={{ p: 0, mr: 1 }} />
                  {user.firstName} {user.lastName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel>Hierarki</InputLabel>
            <Select
              value={hierarchyFilter}
              onChange={(e) => setHierarchyFilter(e.target.value)}
              label="Hierarki"
            >
              <MenuItem value="">Alle</MenuItem>
              <MenuItem value="parents">Kun hovedkontorer</MenuItem>
              <MenuItem value="standalone">Kun selvstaendige</MenuItem>
              <MenuItem value="children">Kun afdelinger</MenuItem>
            </Select>
          </FormControl>
          {(statusFilter.length > 0 || segmentFilter.length > 0 || industryFilter.length > 0 || assignedFilter.length > 0 || hierarchyFilter || letterFilter || debouncedSearch) && (
            <Button
              size="small"
              onClick={() => {
                setStatusFilter([]);
                setSegmentFilter([]);
                setIndustryFilter([]);
                setAssignedFilter([]);
                setHierarchyFilter('');
                setLetterFilter('');
                setSearch('');
              }}
              sx={{ fontSize: '0.7rem', textTransform: 'none' }}
            >
              Ryd filtre
            </Button>
          )}
        </Box>
      </Card>

      {/* Table */}
      <Card>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {columns.map((col) => (
                  <TableCell key={col.id} align={col.align || 'left'}>
                    <TableSortLabel
                      active={sortBy === col.id}
                      direction={sortBy === col.id ? sortOrder : 'asc'}
                      onClick={() => handleSort(col.id)}
                    >
                      {col.label}
                    </TableSortLabel>
                  </TableCell>
                ))}
                <TableCell align="right">Handlinger</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={`sk-${i}`} hover={false}>
                    {Array.from({ length: columns.length + 1 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" width={j === 0 ? '70%' : '50%'} height={18} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : customers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length + 1} align="center" sx={{ py: 5 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                      <PeopleIcon sx={{ fontSize: 32, color: '#60A080' }} />
                      <Typography color="text.secondary" variant="body2">
                        Ingen kunder fundet
                      </Typography>
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                customers.map((customer) => (
                  <TableRow
                    key={customer.id}
                    hover
                    sx={{ cursor: 'pointer', '& td': { py: 0.75 } }}
                    onClick={() => navigate(`/customers/${customer.id}`)}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Avatar sx={{ width: 24, height: 24, bgcolor: 'primary.main', fontSize: '0.65rem' }}>
                          {customer.name.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography
                            component={RouterLink}
                            to={`/customers/${customer.id}`}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                              fontSize: '0.75rem',
                              fontWeight: 600,
                              color: '#208040',
                              textDecoration: 'none',
                              display: 'block',
                              '&:hover': { textDecoration: 'underline', color: '#16602F' },
                            }}
                          >
                            {customer.name}
                          </Typography>
                          {customer.cvr && (
                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.625rem' }}>
                              CVR: {customer.cvr}
                            </Typography>
                          )}
                        </Box>
                        {customer.isPreviousCustomer && (
                          <Chip label="Tidl." size="small" variant="outlined" sx={{ height: 16, fontSize: '0.55rem' }} />
                        )}
                        {(customer as any).source && (customer as any).source !== 'manual' && (
                          <Chip
                            label={(customer as any).source === 'transpas' ? 'Transpas' : (customer as any).source}
                            size="small"
                            variant="outlined"
                            color="info"
                            sx={{ height: 16, fontSize: '0.55rem' }}
                          />
                        )}
                        {(customer as any)._count?.children > 0 && (
                          <Chip
                            icon={<HierarchyIcon sx={{ fontSize: '0.6rem !important' }} />}
                            label={`${(customer as any)._count.children} afd.`}
                            size="small"
                            color="info"
                            variant="outlined"
                            sx={{ height: 16, fontSize: '0.55rem' }}
                          />
                        )}
                        {(customer as any).parent && (
                          <Typography sx={{ fontSize: '0.55rem', color: 'text.secondary' }}>
                            &#x2514; {(customer as any).parent.name}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      {customer.segment && (
                        <Chip
                          label={customer.segment.name}
                          size="small"
                          variant="outlined"
                          sx={{
                            height: 18,
                            fontSize: '0.6rem',
                            borderColor: '#D1DCD5',
                            color: 'text.primary',
                            bgcolor: '#F6FAF7',
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={statusLabels[customer.status] || customer.status}
                        size="small"
                        sx={{
                          bgcolor: statusColors[customer.status] || '#9e9e9e',
                          color: 'white',
                          height: 18,
                          fontSize: '0.6rem',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      {customer.assignedTo ? (
                        <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                          {customer.assignedTo.firstName} {customer.assignedTo.lastName}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {(customer as any).latestActivityDate ? (
                        <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                          {format(new Date((customer as any).latestActivityDate), 'PP', { locale: da })}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {(customer as any).latestBookingDate ? (
                        <Typography variant="body2" sx={{ fontSize: '0.7rem' }}>
                          {format(new Date((customer as any).latestBookingDate), 'PP', { locale: da })}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                          -
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25 }}>
                        {(customer.phone || (customer as any).contacts?.some((c: any) => c.phone)) && (
                          <Tooltip title="Ring">
                            <IconButton
                              size="small"
                              sx={{ p: 0.25 }}
                              onClick={(e) => handleContactAction(e, customer, 'phone')}
                            >
                              <PhoneIcon sx={{ fontSize: '0.9rem' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {(customer.email || (customer as any).contacts?.some((c: any) => c.email)) && (
                          <Tooltip title="Send email">
                            <IconButton
                              size="small"
                              sx={{ p: 0.25 }}
                              onClick={(e) => handleContactAction(e, customer, 'email')}
                            >
                              <EmailIcon sx={{ fontSize: '0.9rem' }} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <IconButton
                          size="small"
                          sx={{ p: 0.25 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMenuOpen(e, customer);
                          }}
                        >
                          <MoreIcon sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 20, 50]}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          labelRowsPerPage="Rækker per side:"
        />
        {/* Alphabet jump bar */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 0.25,
            py: 0.75,
            px: 1,
            borderTop: 1,
            borderColor: 'divider',
            flexWrap: 'wrap',
          }}
        >
          <Typography sx={{ fontSize: '0.65rem', color: 'text.secondary', mr: 0.5 }}>
            Spring til:
          </Typography>
          <Button
            size="small"
            variant={letterFilter === '' ? 'contained' : 'text'}
            onClick={() => setLetterFilter('')}
            sx={{
              minWidth: 24,
              px: 0.5,
              py: 0.25,
              fontSize: '0.65rem',
              textTransform: 'none',
              lineHeight: 1.2,
            }}
          >
            Alle
          </Button>
          {'ABCDEFGHIJKLMNOPQRSTUVWXYZÆØÅ'.split('').map((letter) => (
            <Button
              key={letter}
              size="small"
              variant={letterFilter === letter ? 'contained' : 'text'}
              onClick={() => setLetterFilter(letter === letterFilter ? '' : letter)}
              sx={{
                minWidth: 22,
                px: 0.25,
                py: 0.25,
                fontSize: '0.65rem',
                lineHeight: 1.2,
                fontWeight: letterFilter === letter ? 700 : 500,
              }}
            >
              {letter}
            </Button>
          ))}
        </Box>
      </Card>

      {/* Context Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
        <MenuItem
          onClick={() => {
            navigate(`/customers/${selectedCustomer?.id}`);
            handleMenuClose();
          }}
        >
          <ViewIcon sx={{ mr: 1 }} /> Se detaljer
        </MenuItem>
        <MenuItem
          onClick={() => {
            navigate(`/customers/${selectedCustomer?.id}/edit`);
            handleMenuClose();
          }}
        >
          <EditIcon sx={{ mr: 1 }} /> Rediger
        </MenuItem>
      </Menu>

      {/* Contact Phone/Email Menu */}
      <Menu anchorEl={contactAnchorEl} open={Boolean(contactAnchorEl)} onClose={handleContactMenuClose}>
        {contactMenuCustomer && (() => {
          const contacts = (contactMenuCustomer as any).contacts || [];
          const field = contactMenuType === 'phone' ? 'phone' : 'email';
          const customerValue = contactMenuType === 'phone' ? contactMenuCustomer.phone : contactMenuCustomer.email;
          const prefix = contactMenuType === 'phone' ? 'tel:' : 'mailto:';
          const options: { label: string; value: string }[] = [];
          if (customerValue) {
            options.push({ label: `${contactMenuCustomer.name} (firma)`, value: customerValue });
          }
          for (const c of contacts) {
            if (c[field] && c[field] !== customerValue) {
              const name = [c.firstName, c.lastName].filter(Boolean).join(' ') || 'Kontakt';
              options.push({ label: name, value: c[field] });
            }
          }
          return options.map((opt, i) => (
            <MenuItem
              key={i}
              onClick={() => {
                window.location.href = `${prefix}${opt.value}`;
                handleContactMenuClose();
              }}
            >
              {contactMenuType === 'phone' ? <PhoneIcon sx={{ mr: 1, fontSize: '1rem' }} /> : <EmailIcon sx={{ mr: 1, fontSize: '1rem' }} />}
              <Box>
                <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>{opt.label}</Typography>
                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>{opt.value}</Typography>
              </Box>
            </MenuItem>
          ));
        })()}
      </Menu>
    </Box>
  );
};
